import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Image,
} from "react-native";
import React, { memo, useCallback } from "react";
import { scale } from "@utils/scale";
import color from "@constants/color";
import Text from "@components/AppText";
import t from "@constants/localization";
import CallIcon from "@assets/svg/newCall";
import { normalize } from "@constants/metrics";
import {
  NewTag,
  PaymentMode,
  ShipmentTooltip,
} from "@screens/MyDeliveries/DeliveryOrdersList";
import { fontFamily } from "@utils/styles";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import { ORDER_TRACKING } from "@constants/analyticsEventsCT";
import CustomText from "../CustomText";
import NeededButton from "./NeededButton";

const PATH = "components/InAppRTOModal";

const onPressCall = (phone) => {
  handleCallButtonPress(phone, ORDER_TRACKING.RTO_DELIVERY_CALL, {
    source: "RTO Delivery Card",
  });
};

const RTODeliveryCard = ({
  item = {},
  route = {},
  containerStyle = {},
  onPressNeeded = () => {},
  ordersCount = 0,
}) => {
  const renderItems = useCallback(
    (imageItem, index, arr) => {
      const { product_image = "", id = "" } = imageItem || {};
      if (item?.order_items?.length >= 4 && index === 3) {
        return (
          <Text key={`${id}`} style={styles.productCountText}>
            {`+${item.order_items.length - arr.length + 1}`}
          </Text>
        );
      } else {
        return (
          <View key={`${id}`} style={styles.productImageContainer}>
            <Image
              resizeMode="contain"
              style={styles.productImage}
              source={{
                uri: product_image,
              }}
            />
          </View>
        );
      }
    },
    [item?.order_items],
  );

  return (
    <View style={[styles.listView, containerStyle]}>
      {item?.is_new_user && <NewTag />}
      <View style={styles.view1}>
        <Text style={styles.name}>{item?.user_name}</Text>
        <TouchableOpacity onPress={() => onPressCall(item?.user_phone)}>
          <CallIcon
            strokeColor="#2195F2"
            width={normalize(17)}
            height={normalize(17)}
          />
        </TouchableOpacity>
      </View>
      <Text style={styles.address}>{item?.cx_formatted_address}</Text>
      {item?.payment_status === "COD" ? (
        <View style={styles.view2}>
          <View>
            <Text style={styles.text2}>
              {t(
                `${PATH}/${
                  item?.shipments_list?.length ? "packets" : "orders"
                }`,
              )}
            </Text>
            <ShipmentTooltip list={item?.shipments_list ?? item?.orders_list} />
          </View>
          <View style={styles.separator} />
          <View>
            <View style={{ flexDirection: "row" }}>
              <Text style={styles.text2}>{t(`${PATH}/amount`)}</Text>
              <PaymentMode online={false} />
            </View>
            <Text style={styles.text3}>
              {t(`${PATH}/rs`)}
              {item?.payable_amount}
            </Text>
          </View>
        </View>
      ) : null}
      {item?.payment_status === "PREPAID" ? (
        <View style={styles.view2}>
          <View>
            <Text style={styles.text2}>
              {t(
                `${PATH}/${
                  item?.shipments_list?.length ? "packets" : "orders"
                }`,
              )}
            </Text>
            <ShipmentTooltip list={item?.shipments_list ?? item?.orders_list} />
          </View>
          <View style={styles.separator} />
          <View>
            <View style={{ flexDirection: "row" }}>
              <Text style={styles.text2}>{t(`${PATH}/amount`)}</Text>
              <PaymentMode online={true} />
            </View>
            <Text style={styles.text3}>
              {t(`${PATH}/rs`)}
              {item?.payable_amount}
            </Text>
          </View>
        </View>
      ) : null}
      <ScrollView
        horizontal
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.productItemsContainer}
      >
        {Array.isArray(item?.order_items) && item?.order_items.length !== 0
          ? item?.order_items.slice(0, 4).map(renderItems)
          : null}
      </ScrollView>
      {item?.footerText ? (
        <View style={styles.orderNeededInstructions}>
          <Image
            resizeMode="contain"
            style={styles.footerImage}
            source={{ uri: item?.footerImageUrl }}
          />
          <CustomText
            variant="body_text_2"
            newfont={true}
            style={styles.orderNeeded}
          >
            {item.footerText}
          </CustomText>
        </View>
      ) : null}
      {item?.submitButtons ? (
        <NeededButton
          onPressNeeded={onPressNeeded}
          checkNeededLoading={false}
          checkNotNeededLoading={false}
          containerStyle={styles.neededButtonContainer}
          submitButtons={item?.submitButtons}
        />
      ) : null}
    </View>
  );
};

export default memo(RTODeliveryCard);

const styles = StyleSheet.create({
  listView: {
    padding: 16,
    backgroundColor: color.white,
    borderRadius: 10,
    elevation: 4,
    overflow: "hidden",
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 24,
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  view2: {
    flexDirection: "row",
    marginTop: 12,
    alignItems: "center",
  },
  text2: {
    fontSize: scale(14),
    lineHeight: 20,
    color: "rgba(0, 0, 0, 0.56)",
  },
  text3: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: fontFamily.bold,
    marginRight: 6,
  },
  name: {
    fontSize: scale(14),
    lineHeight: 24,
    color: "black",
    fontWeight: "bold",
  },
  address: {
    maxWidth: "80%",
    color: color.lightBlack,
    fontSize: 14,
    fontFamily: fontFamily.regular,
  },
  tooltipContainer: {
    flexDirection: "row",
    alignItems: "center",
    left: -12,
    paddingHorizontal: 12,
  },
  neededButtonContainer: {
    marginTop: 16,
  },
  separator: {
    backgroundColor: color.lighterBlack,
    height: 24,
    width: 1,
    marginHorizontal: 24,
  },
  productItemsContainer: {
    flexGrow: 1,
    marginTop: 9,
  },
  productCountText: {
    fontSize: 14,
    color: color.lightBlack,
    fontWeight: "500",
    alignSelf: "center",
  },
  itemContainer: {
    marginEnd: scale(16),
  },
  productImageContainer: {
    width: 72,
    height: 72,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: color.searchBg,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    marginEnd: scale(16),
  },
  productImage: {
    width: 68,
    height: 68,
  },
  orderNeededInstructions: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
  },
  orderNeeded: {
    color: color.mediumBlack,
    marginStart: 10,
  },
  footerImage: {
    width: 24,
    height: 24,
  },
});
